/**
 * ==================== ADDRESS AUTOCOMPLETE COMPONENT ====================
 * Componente de autocomplete de endereço usando OpenStreetMap Nominatim API.
 * 
 * Funcionalidades:
 * - Busca sugestões de endereço enquanto o usuário digita
 * - Debounce de 300ms para evitar muitas requisições
 * - Retorna coordenadas (lat/lon) do endereço selecionado
 * - UI estilo Uber com lista de sugestões
 * 
 * SAFE PATCH: Este componente NÃO modifica nenhuma lógica core do sistema.
 * ========================================================================
 */
import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { MapPin, Loader2, X } from 'lucide-react';

const NOMINATIM_API = 'https://nominatim.openstreetmap.org/search';

export default function AddressAutocomplete({ 
  value, 
  onChange, 
  onSelect, 
  placeholder = "Rua, número, bairro, cidade",
  className = ""
}) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [hasSelected, setHasSelected] = useState(false); // Flag to prevent re-fetch after selection
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);

  // Atualizar query quando value mudar externamente
  useEffect(() => {
    if (value !== query) {
      setQuery(value || '');
    }
  }, [value]);

  // Buscar sugestões com debounce
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Don't fetch if user just selected a suggestion
    if (hasSelected) {
      return;
    }

    if (query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      await fetchSuggestions(query);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, hasSelected]);

  // Fechar sugestões ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current && 
        !inputRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Buscar sugestões da API Nominatim
  const fetchSuggestions = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 3) return;

    setLoading(true);
    try {
      // Adicionar "Brasil" para melhorar resultados
      const searchText = searchQuery.toLowerCase().includes('brasil') 
        ? searchQuery 
        : `${searchQuery}, Brasil`;

      const params = new URLSearchParams({
        q: searchText,
        format: 'json',
        addressdetails: '1',
        limit: '5',
        countrycodes: 'br', // Limitar ao Brasil
        'accept-language': 'pt-BR'
      });

      const response = await fetch(`${NOMINATIM_API}?${params}`, {
        headers: {
          'User-Agent': 'ClubeDoCarro/1.0'
        }
      });

      if (!response.ok) throw new Error('Erro na busca');

      const data = await response.json();
      
      // Formatar sugestões
      const formattedSuggestions = data.map((item) => ({
        id: item.place_id,
        label: formatDisplayName(item.display_name),
        fullAddress: item.display_name,
        lat: parseFloat(item.lat),
        lon: parseFloat(item.lon),
        address: item.address
      }));

      setSuggestions(formattedSuggestions);
      setShowSuggestions(formattedSuggestions.length > 0);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Erro ao buscar sugestões:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  // Formatar nome para exibição (remover ", Brasil" e partes repetidas)
  const formatDisplayName = (displayName) => {
    if (!displayName) return '';
    
    // Remover ", Brasil" do final
    let formatted = displayName.replace(/, Brasil$/i, '');
    
    // Limitar tamanho
    if (formatted.length > 80) {
      const parts = formatted.split(', ');
      if (parts.length > 4) {
        formatted = parts.slice(0, 4).join(', ');
      }
    }
    
    return formatted;
  };

  // Handler de input
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setHasSelected(false); // Reset flag when user types again
    setQuery(newValue);
    onChange?.(newValue);
  };

  // Selecionar sugestão
  const handleSelectSuggestion = (suggestion) => {
    setHasSelected(true); // Prevent re-fetch after selection
    setQuery(suggestion.label);
    setSuggestions([]);
    setShowSuggestions(false);
    onChange?.(suggestion.label);
    onSelect?.({
      address: suggestion.label,
      fullAddress: suggestion.fullAddress,
      latitude: suggestion.lat,
      longitude: suggestion.lon
    });
  };

  // Navegação por teclado
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
    }
  };

  // Limpar input
  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    onChange?.('');
    inputRef.current?.focus();
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          className="pr-10"
          data-testid="address-autocomplete-input"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {loading && (
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          )}
          {query && !loading && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          )}
        </div>
      </div>

      {/* Lista de sugestões */}
      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto"
          data-testid="address-suggestions-list"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.id}
              type="button"
              onClick={() => handleSelectSuggestion(suggestion)}
              className={`w-full px-3 py-2.5 text-left flex items-start gap-2 hover:bg-gray-50 transition-colors ${
                index === selectedIndex ? 'bg-gray-100' : ''
              }`}
              data-testid={`address-suggestion-${index}`}
            >
              <MapPin className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
              <span className="text-sm text-gray-700">{suggestion.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Indicador de "digite mais" */}
      {query.length > 0 && query.length < 3 && (
        <p className="text-xs text-muted-foreground mt-1">
          Digite pelo menos 3 caracteres para buscar
        </p>
      )}
    </div>
  );
}

/**
 * Função helper para geocoding de endereço (fallback)
 * Pode ser usada pelo backend se necessário
 */
export async function geocodeAddress(address) {
  if (!address || address.length < 3) return null;

  try {
    const searchText = address.toLowerCase().includes('brasil') 
      ? address 
      : `${address}, Brasil`;

    const params = new URLSearchParams({
      q: searchText,
      format: 'json',
      addressdetails: '1',
      limit: '1',
      countrycodes: 'br'
    });

    const response = await fetch(`${NOMINATIM_API}?${params}`, {
      headers: {
        'User-Agent': 'ClubeDoCarro/1.0'
      }
    });

    if (!response.ok) return null;

    const data = await response.json();
    
    if (data.length === 0) return null;

    return {
      address: data[0].display_name,
      latitude: parseFloat(data[0].lat),
      longitude: parseFloat(data[0].lon)
    };
  } catch (error) {
    console.error('Erro no geocoding:', error);
    return null;
  }
}
